/**
 * One cell scheme per planet (ARCHITECTURE.md §5.3; BUILDLIST 62.10's first step): cube-sphere cells as the
 * address of a place on a planet, and a spatial index over them.
 *
 * A direction falls on one of six cube faces (+x, −x, +y, −y, +z, −z), at gnomonic coordinates (u, v) in
 * [−1, 1]; a face is cut into n × n cells. The layout is surface.ts's terrain bins exactly (`dirToBin`), so a
 * cell id at one n is a terrain bin at the same n. Ids are stable keys: (face · n + i) · n + j.
 *
 * The index answers "what might be within this angle of that direction": each item is filed under the cell
 * of its centre, and a query walks the cells whose bounding cap meets the query cap (breadth first from the
 * centre's cell, across face edges by extending the face plane). It returns a superset, in the items'
 * original order, so a caller that runs its exact test over the candidates gets exactly the answer it got
 * over the full list — the index only skips what cannot be met.
 *
 * Pure: the per-n cell table is derived constant data, memoised by n (its visit stamps are scratch, reset per query).
 */

import type { Vec3 } from '../types';
import { dacos, dcos, dsin } from './dmath';

/** The cell holding a direction (need not be normalised; must not be zero). Matches surface.ts `dirToBin`. */
export function cellOf(x: number, y: number, z: number, n: number): number {
  const ax = Math.abs(x), ay = Math.abs(y), az = Math.abs(z);
  let face: number, u: number, v: number;
  if (ax >= ay && ax >= az) { face = x >= 0 ? 0 : 1; u = y / ax; v = z / ax; }
  else if (ay >= az) { face = y >= 0 ? 2 : 3; u = x / ay; v = z / ay; }
  else { face = z >= 0 ? 4 : 5; u = x / az; v = y / az; }
  let i = Math.floor((u + 1) * 0.5 * n);
  let j = Math.floor((v + 1) * 0.5 * n);
  if (i < 0) i = 0; else if (i >= n) i = n - 1;
  if (j < 0) j = 0; else if (j >= n) j = n - 1;
  return (face * n + i) * n + j;
}

/** The point on face `face`'s plane at gnomonic (u, v) (any values: beyond ±1 it lies over a neighbouring face). */
function facePoint(face: number, u: number, v: number): [number, number, number] {
  switch (face) {
    case 0: return [1, u, v];
    case 1: return [-1, u, v];
    case 2: return [u, 1, v];
    case 3: return [u, -1, v];
    case 4: return [u, v, 1];
    default: return [u, v, -1];
  }
}

function unit(p: [number, number, number]): Vec3 {
  const l = Math.sqrt(p[0] * p[0] + p[1] * p[1] + p[2] * p[2]);
  return { x: p[0] / l, y: p[1] / l, z: p[2] / l };
}

/**
 * The centre direction of cell `cell` at `n` cells per face edge (unit) — computed, not tabled, so a fine n costs
 * nothing until a cell is asked for (62.54 step 2's shelter map).
 */
/** A point inside a cell: (fu, fv) in [0, 1) across its square of the face's grid (0.5, 0.5 is its centre). E5.4's stand anchors. */
export function cellPoint(cell: number, n: number, fu: number, fv: number): Vec3 {
  const j = cell % n, rest = (cell - j) / n, i = rest % n, face = (rest - i) / n;
  return unit(facePoint(face, ((i + fu) / n) * 2 - 1, ((j + fv) / n) * 2 - 1));
}

export function cellCentre(cell: number, n: number): Vec3 {
  const j = cell % n, rest = (cell - j) / n, i = rest % n, face = (rest - i) / n;
  return unit(facePoint(face, ((i + 0.5) / n) * 2 - 1, ((j + 0.5) / n) * 2 - 1));
}

/** Per n: each cell's centre direction, its bounding cap (the largest angle from the centre to a corner) as cos and
 * sin, and its eight neighbours (across face edges too). */
interface CellTable { n: number; cx: Float64Array; cy: Float64Array; cz: Float64Array; cap: Float64Array; capCos: Float64Array; capSin: Float64Array; nb: Int32Array; stamp: Uint32Array; round: number; queue: Int32Array }
const tables = new Map<number, CellTable>();

function cellTable(n: number): CellTable {
  let t = tables.get(n);
  if (t) return t;
  const count = 6 * n * n;
  t = { n, cx: new Float64Array(count), cy: new Float64Array(count), cz: new Float64Array(count), cap: new Float64Array(count), capCos: new Float64Array(count), capSin: new Float64Array(count), nb: new Int32Array(count * 8), stamp: new Uint32Array(count), round: 0, queue: new Int32Array(count) };
  for (let face = 0; face < 6; face++) {
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        const id = (face * n + i) * n + j;
        const u0 = (i / n) * 2 - 1, u1 = ((i + 1) / n) * 2 - 1, v0 = (j / n) * 2 - 1, v1 = ((j + 1) / n) * 2 - 1;
        const c = unit(facePoint(face, (u0 + u1) / 2, (v0 + v1) / 2));
        let minDot = 1;
        for (const [u, v] of [[u0, v0], [u0, v1], [u1, v0], [u1, v1]]) {
          const k = unit(facePoint(face, u, v));
          minDot = Math.min(minDot, c.x * k.x + c.y * k.y + c.z * k.z);
        }
        t.cx[id] = c.x; t.cy[id] = c.y; t.cz[id] = c.z;
        t.cap[id] = dacos(Math.max(-1, Math.min(1, minDot))) * (1 + 1e-9) + 1e-12;   // widened: a bound, never short
        t.capCos[id] = dcos(t.cap[id]); t.capSin[id] = dsin(t.cap[id]);
        // the eight neighbours, across a face edge by extending the face plane past ±1
        let k = 0;
        for (let di = -1; di <= 1; di++) for (let dj = -1; dj <= 1; dj++) {
          if (di === 0 && dj === 0) continue;
          const p = facePoint(face, ((i + di + 0.5) / n) * 2 - 1, ((j + dj + 0.5) / n) * 2 - 1);
          t.nb[id * 8 + k++] = cellOf(p[0], p[1], p[2], n);
        }
      }
    }
  }
  tables.set(n, t);
  return t;
}

/**
 * Build the cell table for `n` now, if it isn't built (it is a process-wide cache, built whole on first use — 6n² cells:
 * 16–30 ms cold at the forest's n of 52 — and nothing reads it but the queries): a caller who knows it will query at `n`
 * builds it where a hitch doesn't show (E5.4: the forest's first band, at match setup). Returns whether it was built here.
 */
export function prepareCells(n: number): boolean {
  if (tables.has(n)) return false;
  cellTable(n);
  return true;
}

/** The cells whose bounding cap meets the cap of `angle` radians round direction `d` (unit), breadth first. */
export function cellsNear(d: Vec3, angle: number, n: number): number[] {
  return cellsNearInto(d, angle, n, []);
}

let lastAngle = NaN, lastCos = 1, lastSin = 0;
/** `cellsNear` into a caller's array (emptied first): the same cells in the same order. */
export function cellsNearInto(d: Vec3, angle: number, n: number, out: number[]): number[] {
  const count = cellsNearBuf(d, angle, n);
  out.length = 0;
  for (let i = 0; i < count; i++) out.push(nearCells[i]);
  return out;
}
/**
 * 62.57: the near cells into a module buffer that only grows (`nearCells`, the first `count` entries) — an array emptied
 * with `length = 0` gave its backing store back to V8 and grew it again on every query (~0.8 MB a tick at 50 a side).
 */
let nearCells = new Int32Array(256);
function cellsNearBuf(d: Vec3, angle: number, n: number): number {
  const t = cellTable(n);
  const start = cellOf(d.x, d.y, d.z, n);
  // a fresh stamp marks this query's visited cells (no per-query set); the table's queue is scratch too
  if (++t.round === 0xffffffff) { t.stamp.fill(0); t.round = 1; }
  const round = t.round;
  t.stamp[start] = round;
  let count = 0;
  const queue = t.queue;
  queue[0] = start;
  let tail = 1;
  // 62.51: callers ask with the same few angles: the last one's cos and sin kept (the same values, without the trig)
  if (angle !== lastAngle) { lastAngle = angle; lastCos = dcos(angle); lastSin = dsin(angle); }
  const ca = lastCos, sa = lastSin;
  const wide = angle >= Math.PI / 2;
  for (let q = 0; q < tail; q++) {
    const id = queue[q];
    // the cell's cap meets the query's when the angle between centres is at most angle + cap:
    // cos(angle + cap) = cos·cos − sin·sin (no transcendental per cell)
    if (!wide && angle + t.cap[id] < Math.PI) {
      const dot = d.x * t.cx[id] + d.y * t.cy[id] + d.z * t.cz[id];
      if (dot < ca * t.capCos[id] - sa * t.capSin[id] - 1e-12) continue;
    }
    if (count === nearCells.length) { const g = new Int32Array(2 * count); g.set(nearCells); nearCells = g; }
    nearCells[count++] = id;
    for (let k = id * 8, e = k + 8; k < e; k++) {
      const nb = t.nb[k];
      if (t.stamp[nb] !== round) { t.stamp[nb] = round; queue[tail++] = nb; }
    }
  }
  return count;
}

/** Items filed by cell: CSR offsets per cell, the item indices grouped by cell. */
export interface CellIndex { n: number; start: Uint32Array; items: Uint32Array; count: number }

/** File `count` items by the cell of each one's direction (`dirOf(i)`, a unit vector). */
export function buildCellIndex(count: number, dirOf: (i: number) => Vec3, n: number): CellIndex {
  return fileCellIndex(cellsOfItems(count, dirOf, n), n);
}

/** E5.4's tail patch: the cell of each item's direction at `n` cells per edge (buildCellIndex's first pass). */
export function cellsOfItems(count: number, dirOf: (i: number) => Vec3, n: number): Uint32Array {
  const cellOfItem = new Uint32Array(count);
  for (let i = 0; i < count; i++) {
    const d = dirOf(i);
    cellOfItem[i] = cellOf(d.x, d.y, d.z, n);
  }
  return cellOfItem;
}

/**
 * E5.4's tail patch: items filed by their cells (buildCellIndex's second pass — a patched table files its kept cells
 * here, the very filing a full build makes): CSR offsets per cell, the item indices ascending within a cell.
 */
let fillScratch = new Uint32Array(0);
export function fileCellIndex(cellOfItem: Uint32Array, n: number): CellIndex {
  const cells = 6 * n * n, count = cellOfItem.length;
  const start = new Uint32Array(cells + 1);
  for (let i = 0; i < count; i++) start[cellOfItem[i] + 1]++;
  for (let c = 0; c < cells; c++) start[c + 1] += start[c];
  // (the running fill positions: a scratch reused, not a copy of `start` a filing — at 128 cells an edge that copy is 384 KB)
  if (fillScratch.length < cells) fillScratch = new Uint32Array(cells);
  const fill = fillScratch;
  fill.set(start.subarray(0, cells));
  const items = new Uint32Array(count);
  for (let i = 0; i < count; i++) items[fill[cellOfItem[i]]++] = i;   // ascending within a cell
  return { n, start, items, count };
}

/**
 * E5.4's tail patch: the value a numeric typed sort puts at position `k` of `a` (`Float64Array.from(…).sort()[k]`, as the
 * full builds read their percentiles), found in O(n) by selection under the sort's own order — −0 before +0 — so it is
 * the same number, bit for bit. Every value must be finite (the caller checks: the sort puts NaN last, a selection would
 * not). Reorders `a`.
 */
export function kthOfSort(a: Float64Array, k: number): number {
  // (−0 before +0: the sort's order; checked only where the values compare equal at zero)
  const before = (x: number, y: number) => x < y || (x === 0 && y === 0 && 1 / x < 0 && 1 / y > 0);
  let lo = 0, hi = a.length - 1;
  while (hi > lo) {
    // the median of three as the pivot, then a three-way partition (equal runs collapse)
    const mid = (lo + hi) >> 1;
    let p = a[mid];
    const x = a[lo], z = a[hi];
    if (before(p, x) !== before(z, x)) p = x; else if (before(p, z) !== before(x, z)) p = z;
    let lt = lo, i = lo, gt = hi;
    while (i <= gt) {
      const v = a[i];
      if (before(v, p)) { a[i] = a[lt]; a[lt] = v; lt++; i++; }
      else if (before(p, v)) { a[i] = a[gt]; a[gt] = v; gt--; }
      else i++;
    }
    if (k < lt) hi = lt - 1; else if (k > gt) lo = gt + 1; else return a[k];
  }
  return a[k];
}

/** Every value finite (no NaN, no ±Infinity): the tail patch's percentiles read only such arrays (else a full build). */
export function allFinite(a: Float64Array): boolean {
  for (let i = 0; i < a.length; i++) if (!Number.isFinite(a[i])) return false;
  return true;
}

/**
 * The items whose direction lies in a cell meeting the cap of `angle` round `d` — a superset of those within
 * `angle` — as ascending indices (the original order), or in cell order when `ordered` is false (cheaper).
 */
export function queryCellIndex(index: CellIndex, d: Vec3, angle: number, ordered = true): number[] {
  const out = queryCellIndexInto(index, d, angle, []);
  return ordered ? out.sort((a, b) => a - b) : out;
}

/** `queryCellIndex` unordered, into a caller's array (emptied first) — for a hot loop that tests every candidate anyway. */
export function queryCellIndexInto(index: CellIndex, d: Vec3, angle: number, out: number[]): number[] {
  const count = queryCellIndexCount(index, d, angle, false);
  out.length = 0;
  for (let i = 0; i < count; i++) out.push(queryIds[i]);
  return out;
}

/**
 * 62.57: the query into a module buffer that only grows: its first `count` entries (`cellQueryIds()`), in cell order, or
 * ascending when `ordered` — the same items `queryCellIndex` gives, nothing allocated. Read them before the next query.
 */
let queryIds = new Uint32Array(1024);
export function queryCellIndexCount(index: CellIndex, d: Vec3, angle: number, ordered: boolean): number {
  const cells = cellsNearBuf(d, angle, index.n);
  let count = 0;
  for (let i = 0; i < cells; i++) {
    const c = nearCells[i];
    for (let k = index.start[c]; k < index.start[c + 1]; k++) {
      if (count === queryIds.length) { const g = new Uint32Array(2 * count); g.set(queryIds); queryIds = g; }
      queryIds[count++] = index.items[k];
    }
  }
  if (ordered && count > 1) queryIds.subarray(0, count).sort();
  return count;
}
/** The buffer queryCellIndexCount filled (its first `count` entries). */
export function cellQueryIds(): Uint32Array { return queryIds; }

/** The cells per face edge for a planet of `radius` whose items are about `size` across: a power of two in [8, 128]. */
export function cellsPerEdge(radius: number, size: number): number {
  // a cell's arc at a face centre is ≈ 2·radius / n; aim for cells a few items across
  const want = (2 * radius) / Math.max(1e-9, 4 * size);
  let n = 8;
  while (n < 128 && n < want) n *= 2;
  return n;
}
